import React, { useEffect } from 'react';
import { styled } from "@mui/material/styles";
import { Autocomplete, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { GlobalButton, GlobalSecondaryButton } from '../../pages/styles';
import ModalTwoButtons from '../Modal/ModalTwoButtons';
import { useUser } from '../../context/UserContext';
import FilterCriteria from '../../api/Global/FilterCriteria';
import ModalSimpleButton from '../Modal/ModalSimpleButton';

const fontSize = 14

const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-input, & .MuiInputLabel-root': {
    fontSize: fontSize,
  },
});

const Option = styled('li')({
  fontSize: fontSize,
});


const FilterManagement = ({moduleName, applyFilterInfo, getFilterInfo }) => {

  const [myFilters, setMyFilters] = React.useState([]);
  const [myFiltersLoading, setMyFiltersLoading] = React.useState(false);
  const [myFilterSelectedId, setMyFilterSelectedId] = React.useState(null);
  const [openModalSaveFilter, setOpenModalSaveFilter] = React.useState(false);
  const [filterToEdit, setFilterToEdit] = React.useState(null);
  const [filterName, setFilterName] = React.useState('');
  const [isDefault, setIsDefault] = React.useState(false);
  const [modalMessagesOpen, setModalMessagesOpen] = React.useState(false);
  const [modalMessagesText, setModalMessagesText] = React.useState("");
  const [modalMessagesTitle, setModalMessagesTitle] = React.useState("");
  const [modalMessagesButtonText, setModalMessagesButtonText] = React.useState("Ok");

  const { username: userEmail } = useUser();

  useEffect(() => {
    getFiltersByModuleAndUser(true);
    // eslint-disable-next-line
  }, [moduleName])

  function getFiltersByModuleAndUser(applyDefault = false) {
    setMyFiltersLoading(true);

    FilterCriteria.getByModule(moduleName).then((res) => {
      setMyFiltersLoading(false);

      const filters = res.map(filter => ({
        ...filter,
        filterInfo: JSON.parse(filter.filterPayload)
      }))
      setMyFilters(filters);
      const filterDefault = filters.find(f => f.isDefault);
      if (filterDefault && applyDefault) {
        handleChangeMyFilterSelected(filterDefault);
      } 
    })
  }

  async function createFilters(filterObject) {
    const filterInfo = getFilterInfo();

    const object = {
      ...filterObject,
      filterModuleName: moduleName,
      filterPayload: JSON.stringify(filterInfo),
      userEmail: userEmail,
    }

    try {
      const result = await FilterCriteria.create(object);
      if (result) {
        const newList = myFilters;

        newList.push({
          ...result,
          filterInfo: filterInfo,
        });

        setMyFilters(newList);
        setMyFilterSelectedId(result.id);
        openModalMessages('Filter Created', 'Filter successfully created');
        getFiltersByModuleAndUser();
      } else {
        openModalMessages('Error Creating', 'Error creating filter\nPlease try again or contact support.');
      }
    } catch (error) {
      openModalMessages('Error Creating', error?.message);
    }
  }

  async function updateFilters(filterObject) {
    const filterInfo = getFilterInfo();

    if (filterObject) {
      const object = {
        ...filterObject,
        filterPayload: JSON.stringify(filterInfo),
      }

      try {
        const result = await FilterCriteria.update(object);
        if (result) {
          const newList = myFilters.filter(f => f.id !== result.id);

          newList.push({
            ...result,
            filterInfo: filterInfo,
          });

          setMyFilters(newList);
          openModalMessages('Filter Updated', 'Filter successfully updated');
          getFiltersByModuleAndUser();
        }
        else {
          openModalMessages('Error Updating', 'Error updating filter\nPlease try again or contact support.');
        }
      } catch (error) {
        openModalMessages('Error Updating', error?.message);
      }
    }
  }

  async function deleteMyFilter(id) {
    try {
      await FilterCriteria.delete(id);
      setMyFilters([...myFilters.filter(f => f.id !== id)]);
      openModalMessages('Filter Deleted', 'Filter successfully deleted');
      setMyFilterSelectedId(null);
    } catch (error) {
      openModalMessages('Error Deleting', error?.message);
    }
  }

  function handleChangeMyFilterSelected(myFilter) {
    setMyFilterSelectedId(myFilter?.id)
    if (myFilter?.filterInfo) {
      applyFilterInfo(myFilter?.filterInfo);
    }
  }

  function handleOpenModalSaveFilter(value, filter = null) {
    if (filter) {
      setFilterName(filter.filterName);
      setIsDefault(filter.isDefault);
    } else {
      setFilterName('');
      setIsDefault(false);
    }
    setFilterToEdit(filter);
    setOpenModalSaveFilter(value);
  }

  function openModalMessages(title, text) {
    setModalMessagesOpen(true);
    setModalMessagesTitle(title);
    setModalMessagesText(text);
    setModalMessagesButtonText("Ok");
  }

  function closeModalMessages() {
    setModalMessagesOpen(false);
    setModalMessagesText("");
    setModalMessagesButtonText("");
    setModalMessagesTitle("");
  }

  return (
    <>        
      {moduleName !== '' && <div style={{
        display: "flex",
        justifyContent: "center",
        gap: 10,
        marginTop: 10,
      }}>
        <StyledAutocomplete
          renderOption={(props2, option) => (
            <Option {...props2}>{option.filterName}{option.isDefault ? ' (Default)' : ''}</Option>
          )}
          loading={myFiltersLoading}
          options={myFilters.sort((a, b) => a.filterName.localeCompare(b.filterName))}
          onChange={(e, value) => handleChangeMyFilterSelected(value)}
          value={myFilterSelectedId ?? null}
          isOptionEqualToValue={(option, value) => value?.id === option?.id}
          autoHighlight
          autoSelect
          style={{ width: "100%", ...styled }}
          getOptionLabel={(option) =>
            option?.filterName ?? myFilters.find((f) => f?.id === option)?.filterName ?? ""
          }
          renderInput={(params) => <TextField {...params} variant="outlined" size="small" label="My Filters" inputProps={{ ...params.inputProps, style: { fontSize: fontSize } }} InputProps={{ ...params.InputProps }} />}
        />
        <GlobalButton
          style={{
            width: 150,
            fontSize: "14px",
            fontFamily: "EMprint",
            textTransform: "none"
          }}
          variant='contained'
          onClick={() => {
            const filterObject = myFilterSelectedId ? myFilters.find(f => f.id === myFilterSelectedId) : null;

            handleOpenModalSaveFilter(true, filterObject)
          }}
          disabled={false}
        >{myFilterSelectedId ? 'Update' : 'Create'}</GlobalButton>

        <GlobalSecondaryButton
          style={{
            fontSize: "14px",
            fontFamily: "EMprint",
            textTransform: "none"
          }}
          variant='contained'
          onClick={() => deleteMyFilter(myFilterSelectedId)}
          disabled={!myFilterSelectedId}
        >Delete</GlobalSecondaryButton>
      </div>}
      <ModalTwoButtons
        title={`${filterToEdit ? 'Update' : 'Create'} Filter`}
        button1Text={filterToEdit ? 'Update' : 'Create'}
        button1Action={() => {
          filterToEdit ? updateFilters({
            ...filterToEdit,
            filterName,
            isDefault
          }) : createFilters({
            filterName,
            isDefault
          });
          handleOpenModalSaveFilter(false);
        }}
        button2Text={'Cancel'}
        button2Action={() => handleOpenModalSaveFilter(false)}
        open={openModalSaveFilter}
        setOpen={handleOpenModalSaveFilter}
      >
        <div style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginTop: 10
        }}>
          <TextField
            style={{ width: "200px" }}
            size="small"
            variant="outlined"
            label="Filter Name"
            inputProps={{ style: { fontSize: fontSize } }}
            InputLabelProps={{ style: { fontSize: fontSize } }}
            InputProps={{ style: { fontSize: fontSize } }}
            value={filterName ?? ''}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(!isDefault)}
              ></Checkbox>
            }
            label={"Is Default?"}
          />
        </div>
      </ModalTwoButtons>
      <ModalSimpleButton
        title={modalMessagesTitle}
        buttonText={modalMessagesButtonText}
        buttonAction={closeModalMessages}
        open={modalMessagesOpen}
        setOpen={setModalMessagesOpen}
      >
        <label style={{ whiteSpace: "break-spaces" }}>
          {modalMessagesText}
        </label>
      </ModalSimpleButton>
    </>
  );
}
export default FilterManagement;


